import {
  Banknote,
  Calendar,
  Check,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  IndianRupee,
  RefreshCw,
  RotateCcw,
  SearchX,
  TrendingUp,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import DatePicker from "@/components/ui/DatePicker";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import StatCard from "@/components/ui/StatCard";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import type { Installment, InstallmentStatus } from "@/types/installment";
import { WALLET_RATES } from "@/pages/retailer/wallet/constants";
import { adminService, installmentService, rocketpayService } from "@/services/apiServices";
import { useInstallmentLiveUpdates } from "@/context/RealtimeEventsContext";
import {
  DEFAULT_RANGE,
  aggregateFailures,
  dayName,
  formatDateTime,
  formatDueDate,
  formatDueShort,
  formatINR,
  inDueRange,
  MONTHS,
  RANGES,
} from "@/pages/retailer/installments/data";
import CollectionTrendChart, { type TrendPoint } from "@/pages/retailer/installments/components/CollectionTrendChart";
import FailureReasons from "@/pages/retailer/installments/components/FailureReasons";
import InstallmentStatusBadge from "@/pages/retailer/installments/components/InstallmentStatusBadge";
import StatusTabs, { TAB_DEFS } from "@/pages/retailer/installments/components/StatusTabs";
import RetryModal from "@/pages/retailer/installments/components/RetryModal";
import SkipModal from "@/pages/retailer/installments/components/SkipModal";
import FilterBar from "./components/FilterBar";
import AdminInstallmentDetailModal from "./components/InstallmentDetailModal";
import { initials, retailerById } from "./data";

function escapeCsvValue(val: unknown): string {
  const s = String(val ?? "");
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(data: (string | number)[][], filename: string) {
  const csvContent = data.map((row) => row.map(escapeCsvValue).join(",")).join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const COLLECTED: InstallmentStatus[] = [
  "COLLECTED",
  "SETTLEMENT_PENDING",
  "SETTLED",
  "SETTLEMENT_FAILED",
  "COLLECTED_OFFLINE",
];

export default function AdminInstallments() {
  return (
    <ToastProvider>
      <InstallmentsInner />
    </ToastProvider>
  );
}

function InstallmentsInner() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const prefilterRef = searchParams.get("ref") ?? "";

  const [loading, setLoading] = useState(true);
  const [syncingAll, setSyncingAll] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const [headerRange, setHeaderRange] = useState(DEFAULT_RANGE);
  const [headerRangeOpen, setHeaderRangeOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const [tab, setTab] = useState("All");
  const [search, setSearch] = useState("");
  const [retailer, setRetailer] = useState("");
  const [bank, setBank] = useState("");
  const [filterRange, setFilterRange] = useState(DEFAULT_RANGE);
  const [filterRangeOpen, setFilterRangeOpen] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTarget, setDetailTarget] = useState<Installment | null>(null);

  const [retryOpen, setRetryOpen] = useState(false);
  const [retryTarget, setRetryTarget] = useState<Installment | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const [skipOpen, setSkipOpen] = useState(false);
  const [skipTarget, setSkipTarget] = useState<Installment | null>(null);
  const [skipLoading, setSkipLoading] = useState(false);

  const [all, setAll] = useState<Installment[]>([]);
  const [retailersList, setRetailersList] = useState<
    { id: string; shopName: string; userCode?: string; name?: string; city?: string }[]
  >([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, retRes] = await Promise.allSettled([
        installmentService.getInstallments({ limit: 5000 }),
        adminService.getRetailers({ limit: 1000 }),
      ]);
      if (res.status === "fulfilled") {
        setAll(res.value?.data?.installments ?? []);
      }
      if (retRes.status === "fulfilled") {
        const list = retRes.value?.data?.retailers ?? retRes.value?.data ?? [];
        setRetailersList(
          list.map((r: any) => ({
            id: r.id || r.retailerId,
            shopName: r.shopName || r.name || "Retailer",
            userCode: r.userCode,
            name: r.name,
            city: r.city,
          }))
        );
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to load installments");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Real-time live update: reload admin installments on webhook or rocketpay events
  useInstallmentLiveUpdates(() => {
    loadData();
  });

  const retailerOptions = useMemo(
    () => [
      { value: "", label: "All Retailers" },
      ...retailersList.map((r) => {
        const code = r.userCode && !r.userCode.includes("-") ? ` (${r.userCode})` : "";
        const owner = r.name && r.name !== r.shopName ? ` • ${r.name}` : "";
        return { value: r.id, label: `${r.shopName}${code}${owner}` };
      }),
    ],
    [retailersList]
  );

  const bankOptions = useMemo(
    () => Array.from(new Set(all.map((t) => t.bankName))).map((b) => ({ value: b, label: b })),
    [all]
  );

  const headerFiltered = useMemo(
    () => all.filter((t) => inDueRange(t.dueDate, headerRange, customFrom, customTo)),
    [all, headerRange, customFrom, customTo]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = all;
    if (q) {
      list = list.filter((t) => {
        const retailerName = t.shopName || t.retailerName || retailerById(t.retailerId)?.shopName || "";
        return `${t.installmentId} ${t.rocketpayInstallmentId ?? ""} ${t.refNo} ${t.customerName} ${retailerName} ${t.utrNumber ?? ""}`
          .toLowerCase()
          .includes(q);
      });
    }
    if (retailer) list = list.filter((t) => t.retailerId === retailer);
    if (bank) list = list.filter((t) => t.bankName === bank);
    if (prefilterRef) list = list.filter((t) => t.refNo === prefilterRef);
    list = list.filter((t) => inDueRange(t.dueDate, filterRange, customFrom, customTo));
    return list;
  }, [all, search, retailer, bank, filterRange, customFrom, customTo, prefilterRef]);

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    TAB_DEFS.forEach((td) => {
      counts[td.key] = td.statuses
        ? filtered.filter((t) => td.statuses!.includes(t.status)).length
        : filtered.length;
    });
    return counts;
  }, [filtered]);

  const tableData = useMemo(() => {
    const active = TAB_DEFS.find((td) => td.key === tab);
    return active?.statuses ? filtered.filter((t) => active.statuses!.includes(t.status)) : filtered;
  }, [filtered, tab]);

  const totalDue = headerFiltered.reduce((s, t) => s + t.amount, 0);
  const collectedList = headerFiltered.filter((t) => COLLECTED.includes(t.status));
  const collected = collectedList.reduce((s, t) => s + t.amount, 0);
  const offlineList = headerFiltered.filter((t) => t.status === "COLLECTED_OFFLINE");
  const offlineAmt = offlineList.reduce((s, t) => s + t.amount, 0);
  const failedList = headerFiltered.filter((t) => t.status === "FAILED");
  const failedAmt = failedList.reduce((s, t) => s + t.amount, 0);
  const platformEarnings = collectedList.reduce((s, t) => s + t.platformCharge, 0);
  const settledList = headerFiltered.filter((t) => t.status === "SETTLED");
  const settled = settledList.reduce((s, t) => s + t.settlementAmount, 0);

  const failureStats = useMemo(() => aggregateFailures(headerFiltered), [headerFiltered]);

  const trendData: TrendPoint[] = useMemo(() => {
    const today = new Date();
    const out: TrendPoint[] = [];
    const pointsCount = headerRange === "This Week" || headerRange === "Last 7 Days" ? 7 : 30;
    for (let i = pointsCount - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const dayItems = all.filter((t) => {
        const d1 = (t.collectionDate || "").slice(0, 10);
        const d2 = (t.offlineCollectedOn || "").slice(0, 10);
        const d3 = (t.presentedAt || "").slice(0, 10);
        const d4 = (t.dueDate || "").slice(0, 10);
        return d1 === key || d2 === key || d3 === key || d4 === key;
      });
      out.push({
        label: formatDueShort(key),
        collected: dayItems.filter((t) => COLLECTED.includes(t.status)).length,
        failed: dayItems.filter((t) => t.status === "FAILED").length,
      });
    }
    return out;
  }, [all, headerRange]);

  const [exporting, setExporting] = useState(false);

  function exportInstallments() {
    if (filtered.length === 0) {
      toast.error("No installments to export with current filters");
      return;
    }
    setExporting(true);
    try {
      const headers = [
        "Installment ID",
        "Gateway Installment ID",
        "Mandate Ref",
        "Customer Name",
        "Customer Mobile",
        "Bank Name",
        "Retailer ID",
        "Retailer / Shop",
        "Amount (INR)",
        "Due Date",
        "Status",
        "Collection Mode",
        "Collection Date",
        "Settlement Status",
        "Settlement Amount",
        "UTR / Ref Number",
        "Failure Reason",
      ];
      const rows = filtered.map((t) => {
        const retailerName = t.shopName || t.retailerName || retailerById(t.retailerId)?.shopName || t.retailerId;
        return [
          t.installmentId,
          t.rocketpayInstallmentId || "",
          t.refNo,
          t.customerName,
          t.customerMobile || "",
          t.bankName,
          t.retailerId,
          retailerName,
          t.amount,
          t.dueDate,
          t.status,
          t.collectionMode || "ONLINE",
          t.collectionDate || "",
          t.status === "SETTLED" ? "SETTLED" : t.status === "COLLECTED" ? "PENDING" : "—",
          t.settlementAmount ?? "",
          t.utrNumber ?? "",
          t.failureReason ?? "",
        ];
      });
      const csv = toCsv(headers, rows);
      downloadCsv(csv, `admin_installments_${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("CSV export downloaded");
    } catch {
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  }

  function resetFilters() {
    setSearch("");
    setRetailer("");
    setBank("");
    setFilterRange(DEFAULT_RANGE);
    setCustomFrom("");
    setCustomTo("");
    setTab("All");
  }

  function openDetail(row: Installment) {
    setDetailTarget(row);
    setDetailOpen(true);
  }

  async function handleRefreshRow(row: Installment) {
    setRefreshingId(row.id);
    try {
      let fresh: Installment | undefined;
      try {
        const rpId = row.rocketpayInstallmentId || row.id;
        const res = await rocketpayService.getInstallmentByIdV4(rpId);
        if (res?.data?.installment) fresh = res.data.installment;
      } catch (_rpErr) {
        const res = await installmentService.getById(row.id);
        fresh = res?.data?.installment;
      }
      if (fresh) {
        setAll((prev) => prev.map((r) => (r.id === fresh!.id ? fresh! : r)));
        setDetailTarget((prev) => (prev && prev.id === fresh!.id ? fresh! : prev));
        toast.success(`Synchronized status: ${fresh.status}`);
      }
    } catch (err: any) {
      toast.error(err?.message || "Could not refresh installment from gateway");
    } finally {
      setRefreshingId(null);
    }
  }

  async function handleSyncAll() {
    setSyncingAll(true);
    try {
      await loadData();
      toast.success("All installments synchronized with latest database records");
    } catch (err: any) {
      toast.error(err?.message || "Failed to sync installments");
    } finally {
      setSyncingAll(false);
    }
  }

  async function scheduleRetry(retryDate: string) {
    if (!retryTarget) return;
    setRetryLoading(true);
    try {
      let schedule_date = retryDate;
      if (retryDate && retryDate.includes(" ")) {
        const parts = retryDate.split(" ");
        if (parts.length === 3) {
          const monthIdx = MONTHS.indexOf(parts[1]) + 1;
          if (monthIdx > 0) {
            schedule_date = `${parts[2]}-${String(monthIdx).padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
          }
        }
      }
      const res = await installmentService.retry(retryTarget.id, schedule_date, retryDate);
      toast.success(res?.message || "Retry scheduled successfully");
      setRetryOpen(false);
      setRetryTarget(null);
      await loadData();
    } catch (err: any) {
      toast.error(err?.message || "Could not schedule the retry");
    } finally {
      setRetryLoading(false);
    }
  }

  async function confirmSkip(reason: string, remarks: string) {
    if (!skipTarget) return;
    setSkipLoading(true);
    try {
      const res = await installmentService.skip(skipTarget.id, remarks ? `${reason} — ${remarks}` : reason);
      toast.success(res?.message || "Installment skipped successfully");
      setSkipOpen(false);
      setSkipTarget(null);
      await loadData();
    } catch (err: any) {
      toast.error(err?.message || "Could not skip this installment");
    } finally {
      setSkipLoading(false);
    }
  }

  const columns: Column<Installment>[] = [
    {
      key: "installmentId",
      header: "Installment ID",
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-xs font-semibold text-ink">{row.installmentId}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard
                .writeText(row.installmentId)
                .then(() => toast.success("Installment ID copied"))
                .catch(() => toast.error("Could not copy"));
            }}
            className="text-ink-muted hover:text-primary-600 p-0.5 cursor-pointer rounded hover:bg-slate-100"
            title="Copy Installment ID"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
    {
      key: "rocketpayInstallmentId",
      header: "Gateway ID",
      render: (row) => {
        const rpId = row.rocketpayInstallmentId;
        if (!rpId) {
          return <span className="text-xs text-[#94A3B8]">—</span>;
        }
        return (
          <div className="flex items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 font-mono text-xs text-primary-700 bg-primary-50 border border-primary-200 px-2 py-0.5 rounded whitespace-nowrap"
              title={rpId}
            >
              <Zap className="h-3 w-3 text-primary-500 shrink-0" />
              {rpId.slice(0, 8)}…{rpId.slice(-4)}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard
                  .writeText(rpId)
                  .then(() => toast.success("Gateway Installment ID copied"))
                  .catch(() => toast.error("Could not copy"));
              }}
              className="text-ink-muted hover:text-primary-600 p-0.5 cursor-pointer rounded hover:bg-slate-100"
              title={`Copy full Gateway ID: ${rpId}`}
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      },
    },
    {
      key: "retailerId",
      header: "Retailer",
      render: (row) => {
        const r = retailersList.find((ret) => ret.id === row.retailerId);
        const displayName = row.shopName || row.retailerName || r?.shopName || "Retailer";
        const sub = r?.userCode && !r.userCode.includes("-") ? r.userCode : (r?.city || r?.name || "");
        return (
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary-500">
              {initials(displayName)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate">{displayName}</p>
              {sub && <p className="text-xs text-ink-secondary truncate">{sub}</p>}
            </div>
          </div>
        );
      },
    },
    {
      key: "refNo",
      header: "Mandate",
      render: (row) => (
        <div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/mandates/${row.refNo}`);
            }}
            className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer text-left"
          >
            {row.refNo}
          </button>
          <p className="text-xs text-ink-secondary">{row.customerName}</p>
        </div>
      ),
    },
    {
      key: "installmentNumber",
      header: "Installment",
      render: (row) => (
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-ink">
            {row.installmentNumber} of {row.totalInstallments}
          </p>
          {row.retryCount > 0 && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#F1F5F9] px-2 py-0.5 text-[11px] font-semibold text-slate-600">
              <RotateCcw className="h-3 w-3" />
              Retry {row.retryCount}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      align: "right",
      render: (row) => <span className="whitespace-nowrap text-sm font-medium text-ink">{formatINR(row.amount)}</span>,
    },
    {
      key: "dueDate",
      header: "Due Date",
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{formatDueDate(row.dueDate)}</p>
          <p className="text-xs text-ink-secondary">{dayName(row.dueDate)}</p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (row) => <InstallmentStatusBadge status={row.status} />,
    },
    {
      key: "collection",
      header: "Collection",
      render: (row) => {
        if (row.status === "COLLECTED_OFFLINE")
          return (
            <div className="flex items-start gap-2">
              <Banknote className="mt-0.5 h-4 w-4 shrink-0 text-[#1E40AF]" />
              <div>
                <p className="text-[13px] text-ink">
                  {row.offlineCollectedOn ? formatDateTime(row.offlineCollectedOn) : "—"}
                </p>
                <p className="text-xs text-ink-secondary">
                  {row.offlineMode}
                  {row.offlineReference ? ` · ${row.offlineReference}` : ""}
                </p>
              </div>
            </div>
          );
        if (COLLECTED.includes(row.status))
          return (
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2.5} />
              <div>
                <p className="text-[13px] text-ink">{row.presentedAt ? formatDateTime(row.presentedAt) : "—"}</p>
                <p className="font-mono text-xs text-ink-secondary">{row.utrNumber ?? "—"}</p>
              </div>
            </div>
          );
        if (row.status === "FAILED")
          return (
            <div className="flex items-start gap-2">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-danger" strokeWidth={2.5} />
              <div>
                <p className="text-xs text-danger">{row.failureReason}</p>
                <p className="text-[11px] text-[#94A3B8]">{row.failureCode}</p>
              </div>
            </div>
          );
        return <span className="text-[#94A3B8]">—</span>;
      },
    },
    {
      key: "settlement",
      header: "Settlement",
      render: (row) => {
        if (row.status === "SETTLED")
          return (
            <div className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2.5} />
              <div>
                <p className="text-[13px] text-ink">{row.settledAt ? formatDateTime(row.settledAt) : "—"}</p>
                <p className="text-xs text-ink-secondary">{formatINR(row.settlementAmount)}</p>
              </div>
            </div>
          );
        if (row.status === "SETTLEMENT_PENDING")
          return (
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 shrink-0 text-warning" />
              <span className="text-[13px] text-ink">In progress</span>
            </div>
          );
        if (row.status === "SETTLEMENT_FAILED")
          return (
            <div className="flex items-center gap-2">
              <X className="h-4 w-4 shrink-0 text-danger" strokeWidth={2.5} />
              <span className="text-[13px] text-danger">Failed</span>
            </div>
          );
        return <span className="text-[#94A3B8]">—</span>;
      },
    },
    {
      key: "charges",
      header: "Charges",
      align: "right",
      render: (row) => {
        if (row.status === "COLLECTED_OFFLINE")
          return (
            <span className="text-xs text-[#64748B]" title="No charge applies to offline collections">
              —
            </span>
          );
        return row.platformCharge > 0 ? (
          <div className="text-right">
            <p className="whitespace-nowrap text-[13px] text-ink">{formatINR(row.platformCharge)}</p>
            <p className="text-xs text-ink-secondary">{formatINR(row.providerCharge)}</p>
          </div>
        ) : (
          <span className="text-[#94A3B8]">—</span>
        );
      },
    },
  ];

  function rowActions(row: Installment) {
    const retryable = row.status === "FAILED";
    const skippable = row.status === "SCHEDULED";
    const isRefreshing = refreshingId === row.id;

    return (
      <>
        <button
          type="button"
          onClick={() => openDetail(row)}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
        >
          <Eye className="h-4 w-4 text-ink-muted" />
          View Details &amp; JSON
        </button>
        <button
          type="button"
          onClick={() => handleRefreshRow(row)}
          disabled={isRefreshing}
          className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-primary-600 hover:bg-primary-50 cursor-pointer"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
          {isRefreshing ? "Syncing..." : "Sync Gateway"}
        </button>
        {retryable && (
          <button
            type="button"
            onClick={() => {
              setRetryTarget(row);
              setRetryOpen(true);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-warning hover:bg-amber-50 cursor-pointer"
          >
            <RotateCcw className="h-4 w-4 text-warning" />
            Retry Schedule
          </button>
        )}
        {skippable && (
          <button
            type="button"
            onClick={() => {
              setSkipTarget(row);
              setSkipOpen(true);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-danger hover:bg-rose-50 cursor-pointer"
          >
            <CheckCircle2 className="h-4 w-4 text-danger" />
            Skip Installment
          </button>
        )}
      </>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Installments</h1>
          <p className="mt-1 text-sm text-ink-secondary">Monitor &amp; Synchronize installments across all retailers</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="secondary"
            onClick={handleSyncAll}
            loading={syncingAll}
            leftIcon={<RefreshCw className={`h-4 w-4 ${syncingAll ? "animate-spin" : ""}`} />}
            title="Synchronize all installments with gateway"
          >
            Sync All Installments
          </Button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setHeaderRangeOpen((v) => !v)}
              className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-slate-300 bg-white px-3.5 text-sm font-medium text-ink hover:bg-slate-50 cursor-pointer"
            >
              <Calendar className="h-4 w-4 text-ink-muted" />
              {headerRange}
            </button>
            {headerRangeOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setHeaderRangeOpen(false)} />
                <div className="absolute right-0 z-20 mt-2 w-44 rounded-lg border border-line bg-white py-1 shadow-dropdown animate-fadeIn">
                  {RANGES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => {
                        setHeaderRange(r);
                        setHeaderRangeOpen(false);
                      }}
                      className={`block w-full whitespace-nowrap px-4 py-2 text-left text-sm cursor-pointer ${
                        headerRange === r ? "bg-primary-50 text-primary-600" : "text-ink hover:bg-slate-50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {headerRange === "Custom Range" && (
            <div className="flex items-center gap-2">
              <div className="w-36">
                <DatePicker value={customFrom} onChange={setCustomFrom} placeholder="From date" />
              </div>
              <span className="text-xs text-ink-muted">to</span>
              <div className="w-36">
                <DatePicker value={customTo} onChange={setCustomTo} placeholder="To date" />
              </div>
            </div>
          )}
          <Button
            variant="secondary"
            onClick={exportInstallments}
            loading={exporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            Export
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 flex flex-col gap-5">
          <TableSkeleton rows={4} columns={4} />
          <TableSkeleton rows={5} columns={10} />
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 sm:gap-4">
            <StatCard
              label="Total Due"
              value={formatINR(totalDue)}
              icon={Calendar}
              iconBg="bg-primary-50"
              iconColor="text-primary-600"
              trendLabel={`${headerFiltered.length} installments`}
              onClick={() => setTab("All")}
            />
            <StatCard
              label="Collected"
              value={formatINR(collected)}
              icon={CheckCircle2}
              iconBg="bg-success-bg"
              iconColor="text-success"
              trendLabel={`${collectedList.length} collected`}
              onClick={() => setTab("Collected")}
            />
            <StatCard
              label="Failed"
              value={formatINR(failedAmt)}
              icon={XCircle}
              iconBg="bg-danger-bg"
              iconColor="text-danger"
              trendLabel={`${failedList.length} installments`}
              onClick={() => setTab("Failed")}
            />
            <StatCard
              label="Settled"
              value={formatINR(settled)}
              icon={Banknote}
              iconBg="bg-[#D1FAE5]"
              iconColor="text-[#047857]"
              trendLabel={`${settledList.length} settled`}
              onClick={() => setTab("Settled")}
            />
            <StatCard
              label="Collected Offline"
              value={formatINR(offlineAmt)}
              icon={Banknote}
              iconBg="bg-[#EFF6FF]"
              iconColor="text-[#1E40AF]"
              trendLabel={`${offlineList.length} offline`}
              onClick={() => setTab("Offline")}
            />
            <StatCard
              label="Platform Earnings"
              value={formatINR(platformEarnings)}
              icon={IndianRupee}
              iconBg="bg-info-bg"
              iconColor="text-info"
              trendLabel={`${collectedList.length} charged`}
              onClick={() => navigate("/admin/reports")}
            />
          </div>

          {/* Two-column section */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[62%_38%]">
            <div className="rounded-[10px] border border-line bg-card p-5 shadow-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-ink">Collection Trend</h3>
                <div className="flex items-center gap-4 text-xs text-ink-secondary">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-success" />
                    Collected
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-danger" />
                    Failed
                  </span>
                </div>
              </div>
              <div className="mt-5">
                <CollectionTrendChart data={trendData} />
              </div>
            </div>
            <FailureReasons stats={failureStats} />
          </div>

          {/* Tabs */}
          <StatusTabs counts={tabCounts} active={tab} onChange={setTab} />

          {/* Filter bar */}
          <FilterBar
            search={search}
            onSearch={setSearch}
            retailer={retailer}
            onRetailer={setRetailer}
            bank={bank}
            onBank={setBank}
            range={filterRange}
            onRange={setFilterRange}
            rangeOpen={filterRangeOpen}
            onRangeOpen={setFilterRangeOpen}
            customFrom={customFrom}
            onCustomFrom={setCustomFrom}
            customTo={customTo}
            onCustomTo={setCustomTo}
            onReset={resetFilters}
            retailerOptions={retailerOptions}
            bankOptions={bankOptions}
          />

          {/* Table */}
          {tableData.length === 0 ? (
            <EmptyState
              icon={SearchX}
              title="No installments found"
              description="Try adjusting your filters or selecting a different tab."
              actionLabel="Reset Filters"
              onAction={resetFilters}
            />
          ) : (
            <DataTable
              columns={columns}
              data={tableData}
              rowKey={(r) => r.id}
              actions={rowActions}
              pageSize={25}
              pageSizeOptions={[25, 50, 100]}
              onRowClick={openDetail}
            />
          )}
        </div>
      )}

      <AdminInstallmentDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        installment={detailTarget}
        mandate={null}
        onRefresh={handleRefreshRow}
        onRetry={(inst) => {
          setDetailOpen(false);
          setRetryTarget(inst);
          setRetryOpen(true);
        }}
        onSkip={(inst) => {
          setDetailOpen(false);
          setSkipTarget(inst);
          setSkipOpen(true);
        }}
        onCopyUtr={(utr) => {
          navigator.clipboard
            .writeText(utr)
            .then(() => toast.success("UTR copied to clipboard"))
            .catch(() => toast.error("Could not copy UTR"));
        }}
      />

      <RetryModal
        open={retryOpen}
        onClose={() => {
          setRetryOpen(false);
          setRetryTarget(null);
        }}
        installment={retryTarget}
        onConfirm={scheduleRetry}
        loading={retryLoading}
      />

      <SkipModal
        open={skipOpen}
        onClose={() => {
          setSkipOpen(false);
          setSkipTarget(null);
        }}
        installment={skipTarget}
        onConfirm={confirmSkip}
        loading={skipLoading}
      />
    </div>
  );
}